import { APP_INITIALIZER, importProvidersFrom, ENVIRONMENT_INITIALIZER, inject, isDevMode, ApplicationConfig } from '@angular/core';
import { ToastrModule } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, withInterceptors, HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { Title, BrowserModule } from '@angular/platform-browser';
import { FormlyModule } from '@ngx-formly/core';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideEffects } from '@ngrx/effects';
import {
  RktFormCheckboxGroupComponent,
  RktFormDatepickerComponent,
  RktFormFieldWrapperComponent,
  RktFormInputCardComponent,
  RktFormInputComponent,
  RktFormInputPasswordComponent,
  RktFormInputPhoneComponent,
  RktFormInputPlaceComponent,
  RktFormInputSecureComponent,
  RktFormRadioGroupComponent,
  RktFormRepeatComponent,
  RktFormRepeatSectionComponent,
  RktFormSelectComponent,
  RktFormSelectCountryComponent,
  RktFormSelectGroupComponent,
  RktFormSelectMultiComponent,
  RktFormSelectStateComponent,
  RktFormTextareaComponent,
  RktFormTimepickerComponent,
  RktFormlyFieldGroupComponent,
  cardNumberValidator,
  countryCodeValidator,
  minLengthValidationMessage,
  phoneNumberValidator,
  requiredValidationMessage,
  stateCodeValidator,
} from '@rocketfinancialcorp/rocket-ui/form';
import {
  arrowDown,
  arrowLeft,
  arrowUp,
  briefcase,
  check,
  chevronDown,
  chevronLeft,
  chevronRight,
  chevronUp,
  close,
  dashboard,
  doubleArrowLeft,
  doubleArrowRight,
  edit,
  filter,
  plus,
  RktIconRegistryService,
  search,
  times,
  trash,
  cog,
  location,
  mailing,
  shipping,
  star,
  checkboxOn,
  checkboxOff,
  error,
  calendar,
  attachment,
  save,
  bank,
  resend,
  card,
  suspend,
  resume,
  link,
  profile,
  info,
  mail,
  arrowRight,
  phone,
  schedule,
  upload,
  passport,
  driverLicense,
  document,
} from '@rocketfinancialcorp/rocket-ui/icon';
import {
  AppRehydrateEffects,
  authEffects,
  authFeature,
  featureFlagEffects,
  featureFlagFeature,
  metaReducers,
  modalEffects,
  teamMembersEffects,
  teamMembersFeature,
  toastEffects,
} from '@store';
import { AuthGuard, NoAuthGuard } from '@shared/guards';
import { getEnvironment, APP_ENV_CONFIG, sentryProviders, MockService } from '@shared/services';
import { SetPageTitle } from '@shared/utils';
import { JwtInterceptor, ErrorInterceptor } from '@shared/interceptors';
import { APP_ROUTES } from './app.routes';

interface RktIcon<T> {
  name: T;
  data: string;
}

const sadCloud: RktIcon<'sad-cloud'> = {
  name: 'sad-cloud',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 4C9.709 4 7.56081 5.13791 6.25781 7.00391 2.78481 7.13191 0 9.997 0 13.5 0 17.084 2.916 20 6.5 20h12c3.032 0 5.5-2.467 5.5-5.5 0-2.964-2.3569-5.38809-5.2949-5.49609C17.8391 6.05591 15.149 4 12 4Zm0 2c2.472 0 4.5446 1.77284 4.9316 4.2148l.1504.961 1.0996-.1446c.105-.0139.2094-.0312.3184-.0312 1.93 0 3.5 1.57 3.5 3.5S20.43 18 18.5 18h-12C4.019 18 2 15.981 2 13.5S4.019 9 6.5 9l.85938.04688.30859-.53321C8.55997 6.96267 10.22 6 12 6Zm-2 4c-.26522 0-.51957.1054-.70711.2929C9.10536 10.4804 9 10.7348 9 11c0 .2652.10536.5196.29289.7071.18754.1875.44189.2929.70711.2929.2652 0 .5196-.1054.7071-.2929S11 11.2652 11 11c0-.2652-.1054-.5196-.2929-.7071S10.2652 10 10 10Zm4 0c-.2652 0-.5196.1054-.7071.2929S13 10.7348 13 11c0 .2652.1054.5196.2929.7071S13.7348 12 14 12c.2652 0 .5196-.1054.7071-.2929S15 11.2652 15 11c0-.2652-.1054-.5196-.2929-.7071S14.2652 10 14 10Zm-2 3c-1.1667 0-2.07801.2958-2.69727.6055-.61925.3096-1.00976.6875-1.00976.6875l1.41406 1.414s.10949-.1221.49027-.3125C10.578 15.2042 11.1667 15 12 15c.8333 0 1.422.2042 1.8027.3945.3808.1904.4903.3125.4903.3125l1.414-1.414s-.3905-.3779-1.0097-.6875C14.078 13.2958 13.1667 13 12 13Z"/></svg>`,
};

const exceptionsList: RktIcon<'exceptions-list'> = {
  name: 'exceptions-list',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="m5.586 3-2 2H2v2h2.414L7 4.414 5.586 3ZM9 5v2h13V5H9ZM5.586 9l-2 2H2v2h2.414L7 10.414 5.586 9ZM9 11v2h13v-2H9Zm-5 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm5 .5v2h13v-2H9Z"/></svg>`,
};

const clock: RktIcon<'clock'> = {
  name: 'clock',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 21 21"><path fill="currentColor" d="M10.5 3C6.35775 3 3 6.35775 3 10.5c0 4.1423 3.35775 7.5 7.5 7.5 4.1423 0 7.5-3.3577 7.5-7.5C18 6.35775 14.6423 3 10.5 3Zm2.4698 11.0303L9.75 10.8105V6h1.5v4.1895l2.7803 2.7803-1.0605 1.0605Z"/></svg>`,
};

const attachmentImg: RktIcon<'attachment-img'> = {
  name: 'attachment-img',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="currentColor" d="M16.667 3.333H3.334c-.921 0-1.667.746-1.667 1.667v10c0 .92.746 1.666 1.667 1.666h13.333c.92 0 1.667-.745 1.667-1.666V5c0-.921-.746-1.667-1.667-1.667ZM8.334 6.666a.834.834 0 1 1 0 1.668.834.834 0 0 1 0-1.668Zm6.666 7.5H5.02a.416.416 0 0 1-.329-.672l2.075-2.668a.416.416 0 0 1 .65-.01l1.752 2.109 2.582-3.327a.416.416 0 0 1 .662.006l2.923 3.896a.417.417 0 0 1-.334.666Z"/></svg>`,
};

const attachmentPdf: RktIcon<'attachment-pdf'> = {
  name: 'attachment-pdf',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="currentColor" d="M5.556 1c-.973 0-1.778.815-1.778 1.8v4.5C2.796 7.3 2 8.105 2 9.1v4.5c0 .995.796 1.8 1.778 1.8v1.8c0 .985.805 1.8 1.778 1.8h10.666c.973 0 1.778-.815 1.778-1.8V5.873a.906.906 0 0 0-.26-.637l-3.924-3.972A.884.884 0 0 0 13.187 1H5.556Zm0 1.8h7.11v2.7c0 .497.399.9.89.9h2.666v.9H5.556V2.8ZM3.778 9.1H5.11c.736 0 1.333.605 1.333 1.35 0 .745-.597 1.35-1.333 1.35h-.444v1.8h-.89V9.1Zm8 0h2.666v.9h-1.777v.9h1.421v.9h-1.421v1.8h-.89V9.1Zm-4.445.021h1.448c1.406 0 2.108.856 2.108 1.895v.694c0 1.052-.703 1.89-2.12 1.89H7.333V9.121ZM4.667 10v.9h.444a.447.447 0 0 0 .445-.45c0-.248-.2-.45-.445-.45h-.444Zm3.555.021V12.7h.547c.562 0 1.231-.172 1.231-.99v-.694c0-.823-.662-.995-1.219-.995h-.559ZM5.556 15.4h10.666v1.8H5.556v-1.8Z"/></svg>`,
};

const refresh: RktIcon<'refresh'> = {
  name: 'refresh',
  data: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 4c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8h-1.6c0 3.529-2.871 6.4-6.4 6.4A6.407 6.407 0 0 1 5.6 12c0-3.529 2.871-6.4 6.4-6.4 1.765 0 3.362.72 4.52 1.88L14.4 9.6H20V4l-2.352 2.352A7.955 7.955 0 0 0 12 4Z"/></svg>',
};

const copy: RktIcon<'copy'> = {
  name: 'copy',
  data: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M4.8 3A1.8 1.8 0 0 0 3 4.8v11.7a.9.9 0 0 0 1.8 0V4.8h11.7a.9.9 0 0 0 0-1.8H4.8Zm3.6 3.6a1.8 1.8 0 0 0-1.8 1.8v10.8A1.8 1.8 0 0 0 8.4 21h10.8a1.8 1.8 0 0 0 1.8-1.8V8.4a1.8 1.8 0 0 0-1.8-1.8H8.4Z"/></svg>',
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideEnvironmentNgxMask(),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([JwtInterceptor])),
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot({
        preventDuplicates: true,
      }),
      FormlyModule.forRoot({
        types: [
          { name: 'formly-group', component: RktFormlyFieldGroupComponent },
          { name: 'input', component: RktFormInputComponent, wrappers: ['field-wrapper'] },
          { name: 'input-phone', component: RktFormInputPhoneComponent, wrappers: ['field-wrapper'] },
          { name: 'input-place', component: RktFormInputPlaceComponent, wrappers: ['field-wrapper'] },
          { name: 'input-password', component: RktFormInputPasswordComponent, wrappers: ['field-wrapper'] },
          { name: 'input-card', component: RktFormInputCardComponent, wrappers: ['field-wrapper'] },
          { name: 'input-secure', component: RktFormInputSecureComponent, wrappers: ['field-wrapper'] },
          { name: 'number', extends: 'input', defaultOptions: { props: { type: 'number' } } },
          { name: 'select', component: RktFormSelectComponent, wrappers: ['field-wrapper'] },
          { name: 'select-country', component: RktFormSelectCountryComponent, wrappers: ['field-wrapper'] },
          { name: 'select-state', component: RktFormSelectStateComponent, wrappers: ['field-wrapper'] },
          { name: 'select-multi', component: RktFormSelectMultiComponent, wrappers: ['field-wrapper'] },
          { name: 'textarea', component: RktFormTextareaComponent, wrappers: ['field-wrapper'] },
          { name: 'radio-group', component: RktFormRadioGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'checkbox-group', component: RktFormCheckboxGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'select-group', component: RktFormSelectGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'repeat', component: RktFormRepeatComponent, wrappers: ['field-wrapper'] },
          { name: 'repeat-section', component: RktFormRepeatSectionComponent, wrappers: ['field-wrapper'] },
          { name: 'datepicker', component: RktFormDatepickerComponent, wrappers: ['field-wrapper'] },
          { name: 'timepicker', component: RktFormTimepickerComponent, wrappers: ['field-wrapper'] },
        ],
        wrappers: [{ name: 'field-wrapper', component: RktFormFieldWrapperComponent }],
        validationMessages: [
          { name: 'required', message: requiredValidationMessage },
          { name: 'minLength', message: minLengthValidationMessage },
        ],
        validators: [
          { name: 'isValidPhoneNumber', validation: phoneNumberValidator },
          { name: 'isValidCountryCode', validation: countryCodeValidator },
          { name: 'isValidStateCode', validation: stateCodeValidator },
          { name: 'isValidCardNumber', validation: cardNumberValidator },
        ],
      }),
      ['develop', 'qa'].includes(getEnvironment().name)
        ? HttpClientInMemoryWebApiModule.forRoot(MockService, { passThruUnknownUrl: true, delay: 200 })
        : [],
    ),
    AuthGuard,
    NoAuthGuard,
    Title,
    { provide: APP_INITIALIZER, useFactory: SetPageTitle, deps: [Router, Title], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: APP_ENV_CONFIG, useFactory: getEnvironment },
    ...sentryProviders(),
    provideAnimations(),
    provideRouter(APP_ROUTES, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () =>
        inject(RktIconRegistryService).registerIcons([
          filter,
          briefcase,
          dashboard,
          arrowLeft,
          chevronLeft,
          chevronRight,
          search,
          times,
          chevronDown,
          chevronUp,
          plus,
          trash,
          doubleArrowRight,
          doubleArrowLeft,
          arrowDown,
          arrowUp,
          close,
          check,
          edit,
          cog,
          sadCloud,
          location,
          mailing,
          shipping,
          star,
          checkboxOn,
          checkboxOff,
          error,
          calendar,
          exceptionsList,
          clock,
          attachmentImg,
          attachmentPdf,
          refresh,
          attachment,
          save,
          bank,
          resend,
          card,
          suspend,
          resume,
          link,
          copy,
          profile,
          info,
          mail,
          arrowRight,
          phone,
          schedule,
          upload,
          passport,
          driverLicense,
          document,
        ]),
    },
    provideStore({ router: routerReducer }, { metaReducers }),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideEffects([authEffects, toastEffects, modalEffects, featureFlagEffects, teamMembersEffects, AppRehydrateEffects]),
    provideState({ name: 'auth', reducer: authFeature.reducer }),
    provideState({ name: 'featureFlag', reducer: featureFlagFeature.reducer }),
    provideState({ name: 'teamMembers', reducer: teamMembersFeature.reducer }),
    provideRouterStore(),
  ],
};
